import React from 'react';
import queryString from 'query-string';
import config from './../../../../../config/config';
import Loading from './../misc/Loading';
import dealerModel from './../../../../model/dealer';
import makesModel from './../../../../model/makes';
import TaskQueueService from './../../../../services/TaskQueueService';
import TaskStatus from './../../../../services/TaskStatus';
import System from './System';
import withRouter from '/src/components/misc/withRouter';

const pollWhileActiveInQueue = (service, offset, limit, callback) => {
  return service.getQueue(offset, limit, true).then(([queue, queueTotal]) => {
    const done = !queue.some((queued) => queued.status === TaskStatus.QUEUED || queued.status === TaskStatus.RUNNING);
    callback(queue, queueTotal);

    if (!done) {
      return new Promise((resolve) => {
        setTimeout(() => {
          resolve(pollWhileActiveInQueue(service, offset, limit, callback));
        }, 750);
      });
    }
  });
};

class SystemContainer extends React.Component {
  constructor(props) {
    super(props);

    const taskQueueConfig = config.taskQueue;
    this.taskQueueService = new TaskQueueService({
      protocol: taskQueueConfig.protocol,
      hostname: taskQueueConfig.hostname,
      port: taskQueueConfig.port,
      pathname: taskQueueConfig.pathname
    });

    this.state = {
      loading: true,
      error: false,
      tasks: {},
      queue: [],
      queueTotal: 0,
      queueLimit: 20,
      queueOffset: 0
    };

    this.handleRunTask = this.handleRunTask.bind(this);
    this.handleSetOffset = this.handleSetOffset.bind(this);

    document.title = 'Admin - System';
  }

  UNSAFE_componentWillMount() { // eslint-disable-line camelcase
    let offset = this.state.queueOffset;

    const query = queryString.parse(this.props.location.search);
    if (query) {
      if (query.page && query.page > 0) {
        offset = (query.page - 1) * this.state.queueLimit;
      }
    }

    Promise.all([
      this.taskQueueService.getQueue(offset, this.state.queueLimit, true),
      this.taskQueueService.getTasks(),
      dealerModel.query('fields.slug,fields.name,fields.useOwnHostname,fields.makes', {
        'fields.skipBuild[ne]': true, // Note: [ne] instead of check of false, because we want to include those with noe value
        // 'fields.useOwnHostname': true, // See filtering below
        'sys.archivedAt[exists]': false,
        'sys.publishedVersion[exists]': true,
        'order': 'fields.name'
      }),
      makesModel.select('fields.slug,fields.name')
    ]).then(([[queue, queueTotal], tasks, dealerSlugs, makes]) => {
      const dealerSlugsFiltered = dealerSlugs.items.filter((ds) => (
        // Make sure dealer has any makes, or is a MF dealer
        (ds.fields.makes || ds.fields.slug['nb-NO'].startsWith('motorforum')) &&
        // Support dealers with useOwnHostname, except for motorforum dealers (which are built in different folders)
        (ds.fields.useOwnHostname['nb-NO'] || ds.fields.slug['nb-NO'].startsWith('motorforum'))
      )).map((ds) => {
        // "Resolve" the dealer makes
        if (!ds.fields.makes?.['nb-NO']) {
          ds.fields.makes = {'nb-NO': []};
        }
        ds.fields.makes['nb-NO'] = ds.fields.makes['nb-NO'].map((dsM) => {
          dsM.fields = makes.items.find((m) => m.sys.id === dsM.sys.id).fields;

          return dsM;
        });

        return ds;
      });

      this.setState({
        tasks: tasks,
        queue: queue,
        dealerSlugs: dealerSlugsFiltered,
        queueTotal: queueTotal,
        queueOffset: offset,
        loading: false
      });

      pollWhileActiveInQueue(this.taskQueueService, this.state.queueOffset, this.state.queueLimit, (queue, queueTotal) => {
        this.setState({
          queue: queue,
          queueTotal: queueTotal
        });
      });
    }).catch((e) => {
      console.error(e);
      this.setState({
        loading: false,
        error: true
      });
    });
  }

  handleSetOffset(offset) {
    this.setState({
      loading: true,
      queueOffset: offset
    }, () => {
      this.props.history.replace({
        pathname: '/system',
        search: queryString.stringify({
          page: Math.floor(offset / this.state.queueLimit) + 1
        })
      });

      pollWhileActiveInQueue(this.taskQueueService, this.state.queueOffset, this.state.queueLimit, (queue, queueTotal) => {
        this.setState({
          queue: queue,
          queueTotal: queueTotal,
          loading: false
        });
      });
    });
  }

  handleRunTask(taskId, params) {
    this.taskQueueService.queueTask(
      taskId,
      params
    ).then(() => {
      return pollWhileActiveInQueue(this.taskQueueService, 0, this.state.queueLimit, (queue, queueTotal) => {
        this.setState({
          queue: queue,
          queueTotal: queueTotal,
          queueOffset: 0
        });
      });
    }).catch((e) => {
      console.error(e);
      this.setState({
        loading: false,
        error: true
      });
    });
  }

  render() {
    if (this.state.loading) {
      return (
        <Loading title="System" text="Laster handlinger og handlingskø..." />
      );
    }

    if (this.state.error) {
      return (
        <div>
          <h1 className="page-header">System</h1>
          <div className="alert alert-danger">
            <strong>Systemfeil:</strong> En feil oppsto ved kommunikasjon med køtjenesten. Vennligst sjekk at tjenesten kjører og er tilgjengelig.
          </div>
        </div>
      );
    }

    return (
      <System
        loading={this.state.loading}
        tasks={this.state.tasks}
        dealerSlugs={this.state.dealerSlugs}
        queue={this.state.queue}
        onRunTask={this.handleRunTask}
        queueTotal={this.state.queueTotal}
        queueOffset={this.state.queueOffset}
        queueLimit={this.state.queueLimit}
        handleSetOffset={this.handleSetOffset}
      />
    );
  }
}

export default withRouter(SystemContainer);
